import { useEffect, Fragment, useState, useMemo } from "react";
import {
  ChevronRightIcon,
  PlusCircleIcon,
  BuildingOfficeIcon,
  Bars2Icon,
  ArrowLeftIcon,
} from "@heroicons/react/24/solid";
import OfficeGeneralInformationTable from "./OfficeGeneralInformationTable";
import OfficeEmployeeTable from "./OfficeEmployeeTable";
import OfficeHierarchyView from "./OfficeHierarchyView";
import OfficeDigitalAssetsTable from "./OfficeDigitalAssetsTable";
import OfficeHoursView from "./OfficeHoursView";
import OfficeCarrierProducerCodeView from "./OfficeCarrierProducerCodeView";
import AgentRecordTable from "./AgentRecordTable";
import OfficeAddressInformationView from "./OfficeAddressInformation";
import OfficeMappingDataView from "./OfficeMappingDataView";
import OfficeBIandAView from "./OfficeBIandAView";
import { Disclosure } from "@headlessui/react";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  DisableOfficeField,
  DisableVendorField,
  officeAddState,
  officeVendorAddState,
  officeVendorEditState,
  officeViewData,
  resetOfficeVendorForm,
} from "../atoms/atoms";
import { IOfficeDetails } from "../types";
import { PERMISSIONS } from "../../../PermissionsConstants";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import AddButton from "../../../../components/AddButton";
import { useHistory, useLocation } from "react-router";
import CloseIconButton from "../../../../components/CloseIconButton";
import { useGetOfficeByOfficeId } from "../hooks/useGetOfficeByOfficeId";
import CustomLoader from "../../../../components/CustomLoader";
import OfficeSystemInformationView from "./OfficeSystemInformationView";
import OfficeDates from "./OfficeDates";
import OfficeFacilitiesView from "./OfficeFacilitiesView";
import { toast } from "../../../../components/toastNotification";
import OfficeLeasedEquipments from "./OfficeLeasedEquipments";
import OfficeBusinessLicensesandPermits from "./OfficeBusinessLicensesandPermits";
import OfficeSecurity from "./OfficeSecurity";
import EditOutlineButton from "../../../../components/EditOutlineButton";
import { Menu, Transition } from "@headlessui/react";
import { mainSelectedTab, paginationData } from "../../atoms";
import {
  businessLicenseFromOffice,
  businessLicenseOfficeId,
  busLicensesAddState,
  busLicensesEditState,
  disableBLOfficeField,
} from "../../BusinessLicensesAndPermits/atoms/atoms";
import {
  disableLEOfficeField,
  leasedEquipmentOfficeId,
  ledAddState,
  leFromOffice,
} from "../../LeasedEquipment/atom/atom";
import {
  securityAddButton,
  SecurityAddState,
  securityFromOffice,
  securityOfficeBrand,
  securityOfficeId,
  securityOfficeState,
  securityOfficeStatus,
} from "../../Security/atoms/atoms";
import OfficeVendorSubModule from "./OfficeVendorSubModule";
import { OfficeVendorServiceModal } from "../modals/OfficeVendorServiceModal";
import OfficeVendorServiceForm from "../forms/OfficeVendorServiceForm";

import { OfficeTabs } from "../../../OfficeTab/OfficeTabs";
import { entityEIN, vendorServiceId } from "../../Vendor/atoms/atoms";
import OfficeImageSection from "./OfficeImageSection";
import OfficeTelecomView from "./OfficeTelecomView";
import { getOfficeHierarchyById } from "actions/OfficeHierarchy";
import SPLCHierarchyView from "./SPLCHierarchyView";
import { useGetParentHierarchyById } from "modules/CarrierAgreementMgmt/Hierarchy/hooks/useGetParentHierarchyById";
import { OfficeApplianceSideSection } from "./OfficeApplianceSideSection";
import {
  applianceAddState,
  applianceOfficeId,
  showApplianceFromOffice,
} from "modules/CarrierAgreementMgmt/OfficeAppliance/atom";
import { OFFICE_PERMISSIONS } from "../permission";

// FIX 1: Define Interface for Location State
interface LocationState {
  officeId?: number;
  action?: string;
  [key: string]: any;
}

const OfficeViews = ({
  onClickCancelShow,
  handleEdit,
  confieEdit,
  franchiseEdit,
}: any) => {
  const rowViewData = useRecoilValue<IOfficeDetails>(officeViewData);

  // FIX 2: Apply Interface to useLocation
  const location = useLocation<LocationState>();
  const history = useHistory();

  // Initialize persistedId checking location.state first, then localStorage
  const [persistedId, setPersistedId] = useState<string | number | null>(() => {
    return (
      location?.state?.officeId ||
      localStorage.getItem("currentOfficeId") ||
      null
    );
  });

  // FIX 3: Type State to allow boolean or null
  const [showLink, setShowLink] = useState<boolean | null>(null);

  // Memoize the office ID selection logic
  const officeIdToFetch = useMemo(() => {
    // 1. Priority: Recoil State (if active and set)
    if (rowViewData?.officeId) {
      return rowViewData.officeId;
    }
    // 2. Priority: Navigation State (Redirection from EmployeeTable)
    else if (location?.state?.officeId) {
      return Number(location.state.officeId);
    }
    // 3. Priority: Local Storage (Page refresh)
    else if (persistedId) {
      return Number(persistedId);
    } else {
      return 0;
    }
  }, [rowViewData, persistedId, location.state]);

  // Sync Logic: Ensure LocalStorage is always up to date with the current view
  useEffect(() => {
    if (location?.state?.officeId) {
      localStorage.setItem(
        "currentOfficeId",
        location.state.officeId.toString()
      );
      setPersistedId(location.state.officeId);
    } else if (rowViewData?.officeId) {
      setPersistedId(rowViewData.officeId.toString());
      localStorage.setItem("currentOfficeId", rowViewData.officeId.toString());
    }
  }, [rowViewData, location.state]);

  const {
    data: officeData,
    isFetching,
    isSuccess,
  } = useGetOfficeByOfficeId(officeIdToFetch);

  const { data: hierarchyData } = useGetParentHierarchyById(
    officeData?.splcHierarchyId,
    false
  );

  const checker = async (id: number) => {
    try {
      const result = await getOfficeHierarchyById(id);
      if (typeof result == "string") {
        setShowLink(true);
      } else {
        setShowLink(false);
      }
    } catch {
      setShowLink(false);
    }
  };

  useEffect(() => {
    if (isSuccess) {
      if (officeData?.statusCode) {
        toast.fire("Server Error", "Cannot retrieve Office Data", "error");
      }
    }
    if (officeData?.officeHierarchyId) {
      checker(officeData.officeHierarchyId);
    } else {
      setShowLink(false);
    }
  }, [isSuccess, officeData]);

  const [, setAddState] = useRecoilState(officeAddState);
  const userPermissions = useRecoilValue(permissionsSelector);
  const addPermission =
    OFFICE_PERMISSIONS.permission_carrieragreemnentmgmt_office_add;
  const viewGeneralInfoPermission =
    OFFICE_PERMISSIONS.permission_office_generalinformation_view;
  const viewCarrierProdCodePermission =
    OFFICE_PERMISSIONS.permission_office_carrierproducercodes_view;
  const viewOfficeHoursPermission =
    OFFICE_PERMISSIONS.permission_office_officehours_view;
  const viewOfficeHierarchyPermission =
    OFFICE_PERMISSIONS.permission_office_officehierarchy_view;
  const viewEmployeeInfoPermission =
    OFFICE_PERMISSIONS.permission_office_employeeinformation_view;

  const viewAgentOfRecordPermission =
    OFFICE_PERMISSIONS.permission_office_agentofrecords_view;
  const viewDigitalAssetsPermission =
    PERMISSIONS.permission_carrieragreemnentmgmt_digitalassets_view;
  const viewOfficeAddress = OFFICE_PERMISSIONS.permission_office_address_view;
  const viewOfficeDates = OFFICE_PERMISSIONS.permission_office_dates_view;
  const viewOfficeBIA = OFFICE_PERMISSIONS.permission_office_bia_view;

  const viewLeasedEquipment = PERMISSIONS.permission_leasedequipment_view;
  const viewBusLicensePermit =
    PERMISSIONS.permission_buslicensesandpermits_view;
  const viewSecurity = PERMISSIONS.permission_security_view;
  const viewOfficeVendor = OFFICE_PERMISSIONS.permission_office_vendor_view;
  const addOfficeVendor = OFFICE_PERMISSIONS.permission_office_vendor_add;
  const addDisabledBLP = PERMISSIONS.permission_buslicensesandpermits_add;
  const addDisabledSecurity = PERMISSIONS.permission_security_add;
  const addDisabledLE = PERMISSIONS.permission_leasedequipment_add;
  const viewTelecom = OFFICE_PERMISSIONS.permission_office_telecom_view;
  const addAppliancePermission = PERMISSIONS.permission_officeappliance_add;
  const viewAppliancePermission = PERMISSIONS.permission_officeappliance_view;

  const viewSplcHierarchyPermission = PERMISSIONS.permission_hierarchy_view;

  const [, setSelectedIndex] = useRecoilState(mainSelectedTab);
  const [, setBLPAddState] = useRecoilState(busLicensesAddState);
  const [, setEditState] = useRecoilState(busLicensesEditState);
  const [, setLEAddState] = useRecoilState(ledAddState);
  const [, setSecurityAddState] = useRecoilState(SecurityAddState);
  const [, setShowFromOffice] = useRecoilState(businessLicenseFromOffice);
  const [, setShowFromLEOffice] = useRecoilState(leFromOffice);
  const [, setShowSecurityFromOffice] = useRecoilState(securityFromOffice);
  const [, setShowApplianceFromOffice] = useRecoilState(
    showApplianceFromOffice
  );
  const [, setIsAddState] = useRecoilState(applianceAddState);
  const [, setApplianceOfficeId] = useRecoilState(applianceOfficeId);
  const [, setBusinessLicenseOfficeId] = useRecoilState(
    businessLicenseOfficeId
  );
  const [, setleOfficeId] = useRecoilState(leasedEquipmentOfficeId);
  const [, setSecurityOfficeStatus] = useRecoilState(securityOfficeStatus);
  const [, setSecurityOfficeBrand] = useRecoilState(securityOfficeBrand);
  const [, setSecurityOfficeState] = useRecoilState(securityOfficeState);
  const [, setSecurityOfficeId] = useRecoilState(securityOfficeId);
  const [isResetOfficeVendorForm, setResetOfficeVendorForm] = useRecoilState(
    resetOfficeVendorForm
  );
  const [isOfficeVendorServiceAddState, setOfficeVendorServiceAddState] =
    useRecoilState(officeVendorAddState);
  const [isOfficeVendorServiceEditState, setOfficeVendorServiceEditState] =
    useRecoilState(officeVendorEditState);
  const [, setDisableOfficeField] = useRecoilState(DisableOfficeField);
  const [, setDisableVendorField] = useRecoilState(DisableVendorField);

  const [disableSecurityAddButton] = useRecoilState(securityAddButton);
  const [, setDisableBLOfficeField] = useRecoilState(disableBLOfficeField);
  const [, setDisableLEOfficeField] = useRecoilState(disableLEOfficeField);
  const [, setVendorId] = useRecoilState(vendorServiceId);
  const [, setEntityEin] = useRecoilState(entityEIN);
  const [page, setPage] = useRecoilState(paginationData);

  const handleCloseView = () => {
    localStorage.removeItem("currentOfficeId");
    onClickCancelShow();
    setPage(page);
  };

  const onClickBLPAddButton = () => {
    setBusinessLicenseOfficeId(officeData.officeId);
    setDisableBLOfficeField(true);
    setEditState(false);
    setShowFromOffice(true);
    setBLPAddState(true);

    handleLink("buslicensesandpermits", "table");
  };

  const onClickLEAddButton = () => {
    handleLink("leasedequipment");
    setDisableLEOfficeField(true);
    setLEAddState(true);
    setEditState(false);
    setShowFromLEOffice(true);
    setleOfficeId(officeData.officeId);
  };

  const onClickOfficeVendorModal = () => {
    setOfficeVendorServiceAddState(true);
    setDisableOfficeField(true);
    setEditState(false);
    setVendorId(officeData.vendorId);
    setEntityEin(officeData?.entityEIN);
  };

  const closeOfficeVendorModal = () => {
    setResetOfficeVendorForm(isResetOfficeVendorForm ? false : true);
    setOfficeVendorServiceAddState(false);
    setOfficeVendorServiceEditState(false);
    setDisableOfficeField(false);
    setDisableVendorField(false);
    setVendorId(undefined);
  };
  const onClickSecurityAddButton = () => {
    handleLink("security");
    setSecurityAddState(true);
    setEditState(false);
    setShowSecurityFromOffice(true);
    setSecurityOfficeId(officeData.officeId);
    setSecurityOfficeBrand(officeData.brandName);
    setSecurityOfficeState(officeData.stateCode);
    setSecurityOfficeStatus(officeData.officeStatus);
  };

  const onClickAddOfficeAppliance = () => {
    handleLink("officeappliance");
    setIsAddState(true);
    setShowApplianceFromOffice(true);
    setApplianceOfficeId(officeData.officeId);
  };
  type DestinationView = "dashboard" | "table" | "recent";

  const handleLink = (
    moduleName: string,
    destinationView?: DestinationView
  ) => {
    const pathname = `/officesettings/${moduleName}`;

    const search =
      destinationView && destinationView !== "dashboard"
        ? `?view=${destinationView}`
        : "";

    const userTabs = OfficeTabs.filter((tab) =>
      userPermissions.includes(tab.permission)
    );

    const index = userTabs.findIndex((tab) => tab.to === pathname);

    setSelectedIndex(index);

    history.push(`${pathname}${search}`);
  };
  if (isFetching) {
    return (
      <div className="relative w-full py-2 box-shadow border-quinary border-l-2">
        <CustomLoader />
      </div>
    );
  }

  if (!officeData?.officeId) {
    return (
      <div className="relative w-full py-10 box-shadow border-quinary border-l-2 flex flex-col items-center justify-center">
        <h3 className="text-gray-500 font-bold mb-4">
          No Office Selected or Data Not Found.
        </h3>
        <button
          onClick={handleCloseView}
          className="flex items-center text-blue-500 hover:text-blue-700 underline"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Go Back to Search
        </button>
      </div>
    );
  }

  return (
    <div className="relative w-full py-2 box-shadow border-quinary border-l-2">
      <CloseIconButton onClick={handleCloseView} />
      <div className="flex mx-4 justify-between pb-1 border-secondary border-b-2">
        <div className="flex gap-2 items-center">
          <div>
            <BuildingOfficeIcon className="text-white bg-blue-500 rounded-md h-7 w-7 p-1" />
          </div>
          {!isFetching && (
            <div className="flex flex-col leading-tight">
              <div className="blueAndBold ">{officeData?.officeName}</div>
              <div className="text-xs font-medium text-gray-500">
                {officeData?.address} {officeData?.city},{" "}
                {officeData?.stateCode}, {officeData?.zipCode},{" "}
                {officeData?.countryCode}
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-between mt-4">
          <div className="flex items-center gap-2 pb-2">
            {userPermissions.includes(addPermission) && (
              <AddButton buttonText="Add" onClick={() => setAddState(true)} />
            )}
            {((officeData?.officeType !== "Franchise" && confieEdit) ||
              (officeData?.officeType === "Franchise" && franchiseEdit)) && (
              <EditOutlineButton
                buttonText="Edit"
                className={undefined}
                onClick={() => handleEdit(officeData)}
                disabled={false}
              />
            )}
          </div>
        </div>
      </div>
      {!isFetching && showLink !== null ? (
        <>
          <div
            className={
              userPermissions.includes(viewBusLicensePermit) ||
              userPermissions.includes(viewLeasedEquipment) ||
              userPermissions.includes(viewSecurity) ||
              userPermissions.includes(viewOfficeVendor)
                ? "grid grid-cols-4 "
                : ""
            }
          >
            <div className="col-span-3">
              {userPermissions.includes(viewGeneralInfoPermission) && (
                <div className="mt-4 mb-2 ml-4 mr-2 bg-white rounded-lg box-shadow">
                  <Disclosure defaultOpen>
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="disclosureBtn ml-2">
                          <ChevronRightIcon
                            className={`${
                              open ? "transform rotate-90" : ""
                            } text-gray-500 w-5 h-5 `}
                          />
                          <div className="disclosureLabel ml-2">
                            General Information
                          </div>
                        </Disclosure.Button>

                        <Disclosure.Panel className="text-xs bg-white">
                          <OfficeGeneralInformationTable
                            data={officeData}
                            checker={showLink ? "true" : "false"}
                          />
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                </div>
              )}
              {officeData?.channelC !== "SPLC"
                ? userPermissions.includes(viewOfficeHierarchyPermission) && (
                    <div className="ml-4 mr-2 mb-2 mx-4 bg-white rounded-lg box-shadow">
                      <Disclosure defaultOpen>
                        {({ open }) => (
                          <>
                            <Disclosure.Button className="disclosureBtn ml-2">
                              <ChevronRightIcon
                                className={`${
                                  open ? "transform rotate-90" : ""
                                } w-5 h-5 text-gray-500`}
                              />
                              <div className="disclosureLabel ml-2">
                                {"Office Hierarchy & Leadership"}
                              </div>
                            </Disclosure.Button>

                            <Disclosure.Panel className="text-xs bg-white">
                              {<OfficeHierarchyView data={officeData} />}
                            </Disclosure.Panel>
                          </>
                        )}
                      </Disclosure>
                    </div>
                  )
                : userPermissions.includes(viewSplcHierarchyPermission) && (
                    <div className="ml-4 mr-2 mb-2 mx-4 bg-white rounded-lg box-shadow">
                      <Disclosure defaultOpen>
                        {({ open }) => (
                          <>
                            <Disclosure.Button className="disclosureBtn ml-2">
                              <ChevronRightIcon
                                className={`${
                                  open ? "transform rotate-90" : ""
                                } w-5 h-5 text-gray-500`}
                              />
                              <div className="disclosureLabel ml-2">
                                {"SPLC Hierarchy & Leadership"}
                              </div>
                            </Disclosure.Button>

                            <Disclosure.Panel className="text-xs bg-white">
                              {
                                <SPLCHierarchyView
                                  data={officeData}
                                  hierarchyData={hierarchyData}
                                />
                              }
                            </Disclosure.Panel>
                          </>
                        )}
                      </Disclosure>
                    </div>
                  )}
              {userPermissions.includes(viewOfficeAddress) && (
                <>
                  <div className="ml-4 mr-2 mb-2 mx-4 bg-white rounded-lg box-shadow">
                    <Disclosure defaultOpen>
                      {({ open }) => (
                        <>
                          <Disclosure.Button className="disclosureBtn ml-2">
                            <ChevronRightIcon
                              className={`${
                                open ? "transform rotate-90" : ""
                              } w-5 h-5 text-gray-500`}
                            />
                            <div className="disclosureLabel ml-2">
                              {"Address Information"}
                            </div>
                          </Disclosure.Button>
                          <Disclosure.Panel className="text-xs bg-white">
                            {
                              <OfficeAddressInformationView
                                data={{
                                  ...officeData,
                                }}
                              />
                            }
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  </div>

                  <div className="ml-4 mr-2 mb-2 mx-4 bg-white rounded-lg box-shadow">
                    <Disclosure defaultOpen>
                      {({ open }) => (
                        <>
                          <Disclosure.Button className="disclosureBtn ml-2">
                            <ChevronRightIcon
                              className={`${
                                open ? "transform rotate-90" : ""
                              } w-5 h-5 text-gray-500`}
                            />
                            <div className="disclosureLabel ml-2">
                              {"Mapping Data (Admin)"}
                            </div>
                          </Disclosure.Button>
                          <Disclosure.Panel className="text-xs ">
                            {<OfficeMappingDataView data={officeData} />}
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  </div>
                </>
              )}
              <div className="grid grid-cols-2 mt-2">
                {userPermissions.includes(viewOfficeHoursPermission) && (
                  <div className="ml-4 mr-2 mb-2 mx-4 bg-white rounded-lg box-shadow">
                    <Disclosure defaultOpen>
                      {({ open }) => (
                        <>
                          <Disclosure.Button className="disclosureBtn ml-2">
                            <ChevronRightIcon
                              className={`${
                                open ? "transform rotate-90" : ""
                              } w-5 h-5 text-gray-500`}
                            />
                            <div className="disclosureLabel ml-2">
                              {"Office Hours"}
                            </div>
                          </Disclosure.Button>

                          <Disclosure.Panel className="text-xs ">
                            {<OfficeHoursView data={officeData} />}
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  </div>
                )}
                <div className="mr-2 mb-2 bg-white rounded-lg box-shadow">
                  <Disclosure defaultOpen>
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="disclosureBtn ml-2">
                          <ChevronRightIcon
                            className={`${
                              open ? "transform rotate-90" : ""
                            } w-5 h-5 text-gray-500`}
                          />
                          <div className="disclosureLabel ml-2">
                            {"Facilities Management"}
                          </div>
                        </Disclosure.Button>
                        <Disclosure.Panel className="text-xs ">
                          {<OfficeFacilitiesView data={officeData} />}
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                </div>
              </div>
              <div className="grid grid-cols-2 ">
                <div>
                  {userPermissions.includes(viewOfficeDates) && (
                    <div className="ml-4 mr-2 bg-white rounded-lg box-shadow">
                      <Disclosure defaultOpen>
                        {({ open }) => (
                          <>
                            <Disclosure.Button className="disclosureBtn ml-2">
                              <ChevronRightIcon
                                className={`${
                                  open ? "transform rotate-90" : ""
                                } w-5 h-5 text-gray-500`}
                              />
                              <div className="disclosureLabel ml-2">
                                {"Dates"}
                              </div>
                            </Disclosure.Button>
                            <Disclosure.Panel className="text-xs ">
                              {<OfficeDates data={officeData} />}
                            </Disclosure.Panel>
                          </>
                        )}
                      </Disclosure>
                    </div>
                  )}
                </div>
                <div>
                  {" "}
                  {userPermissions.includes(viewOfficeBIA) && (
                    <div className="mr-2 mb-2 bg-white rounded-lg box-shadow">
                      <Disclosure defaultOpen>
                        {({ open }) => (
                          <>
                            <Disclosure.Button className="disclosureBtn ml-2">
                              <ChevronRightIcon
                                className={`${
                                  open ? "transform rotate-90" : ""
                                } w-5 h-5 text-gray-500`}
                              />
                              <div className="disclosureLabel ml-2">
                                {"BI&A"}
                              </div>
                            </Disclosure.Button>
                            <Disclosure.Panel className="text-xs py-2">
                              {<OfficeBIandAView data={officeData} />}
                            </Disclosure.Panel>
                          </>
                        )}
                      </Disclosure>
                    </div>
                  )}
                </div>
              </div>

              {userPermissions.includes(viewTelecom) && (
                <div className="ml-4 mr-2 mb-2 mx-4 bg-white rounded-lg box-shadow">
                  <Disclosure defaultOpen>
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="disclosureBtn ml-2">
                          <ChevronRightIcon
                            className={`${
                              open ? "transform rotate-90" : ""
                            } w-5 h-5 text-gray-500`}
                          />
                          <div className="disclosureLabel ml-2">Telecom</div>
                        </Disclosure.Button>

                        <Disclosure.Panel className="text-xs">
                          {<OfficeTelecomView data={officeData} />}
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                </div>
              )}

              {userPermissions.includes(viewCarrierProdCodePermission) && (
                <div className="ml-4 mr-2 mb-2 mx-4 bg-white rounded-lg box-shadow">
                  <Disclosure defaultOpen>
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="disclosureBtn ml-2">
                          <ChevronRightIcon
                            className={`${
                              open ? "transform rotate-90" : ""
                            } w-5 h-5 text-gray-500`}
                          />
                          <div className="disclosureLabel ml-2">
                            Carrier Producer Codes
                          </div>
                        </Disclosure.Button>

                        <Disclosure.Panel className="text-xs">
                          {<OfficeCarrierProducerCodeView data={officeData} />}
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                </div>
              )}
              {userPermissions.includes(viewEmployeeInfoPermission) && (
                <div className="ml-4 mr-2 mb-2 mx-4 bg-white rounded-lg box-shadow">
                  <Disclosure defaultOpen>
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="disclosureBtn ml-2">
                          <ChevronRightIcon
                            className={`${
                              open ? "transform rotate-90" : ""
                            } w-5 h-5 text-gray-500`}
                          />
                          <div className="disclosureLabel ml-2">
                            Employee Information
                          </div>
                        </Disclosure.Button>

                        <Disclosure.Panel className="text-xs">
                          {<OfficeEmployeeTable data={officeData} />}
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                </div>
              )}

              {userPermissions.includes(viewAgentOfRecordPermission) && (
                <div className="ml-4 mr-2 mx-2 my-2 pb-2 bg-white rounded-lg box-shadow">
                  <Disclosure defaultOpen>
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="disclosureBtn ml-2">
                          <ChevronRightIcon
                            className={`${
                              open ? "transform rotate-90" : ""
                            } w-5 h-5 text-gray-500`}
                          />
                          <div className="disclosureLabel ml-2">
                            Agent of Records
                          </div>
                        </Disclosure.Button>

                        <Disclosure.Panel className="text-xs">
                          <AgentRecordTable
                            data={[]}
                            officeId={
                              officeData?.officeId ? officeData?.officeId : 0
                            }
                          />
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                </div>
              )}
              {userPermissions.includes(viewDigitalAssetsPermission) && (
                <div className="ml-4 mr-2 mb-2 mx-4 bg-white rounded-lg box-shadow">
                  <Disclosure defaultOpen>
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="disclosureBtn ml-2">
                          <ChevronRightIcon
                            className={`${
                              open ? "transform rotate-90" : ""
                            } w-5 h-5 text-gray-500`}
                          />
                          <div className="disclosureLabel ml-2">
                            {"Digital Assets"}
                          </div>
                        </Disclosure.Button>

                        <Disclosure.Panel className="text-xs bg-white">
                          {<OfficeDigitalAssetsTable data={officeData} />}
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                </div>
              )}
              <div className="ml-4 mr-2 mb-2 bg-white rounded-lg box-shadow">
                <Disclosure defaultOpen>
                  {({ open }) => (
                    <>
                      <Disclosure.Button className="disclosureBtn ml-2">
                        <ChevronRightIcon
                          className={`${
                            open ? "transform rotate-90" : ""
                          } w-5 h-5 text-gray-500`}
                        />
                        <div className="disclosureLabel ml-2">
                          {"Office Image Section"}
                        </div>
                      </Disclosure.Button>
                      <Disclosure.Panel className="text-xs ">
                        {<OfficeImageSection data={officeData} />}
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              </div>
              <div className="ml-4 mr-2 mb-2 bg-white rounded-lg box-shadow">
                <Disclosure defaultOpen>
                  {({ open }) => (
                    <>
                      <Disclosure.Button className="disclosureBtn ml-2">
                        <ChevronRightIcon
                          className={`${
                            open ? "transform rotate-90" : ""
                          } w-5 h-5 text-gray-500`}
                        />
                        <div className="disclosureLabel ml-2">
                          {"System Information"}
                        </div>
                      </Disclosure.Button>
                      <Disclosure.Panel className="text-xs ">
                        {<OfficeSystemInformationView data={officeData} />}
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              </div>
            </div>
            {/* SAMPLE SIDEBAR */}
            <div>
              {userPermissions.includes(viewBusLicensePermit) && (
                <div className="mt-4 mb-2 mr-4 bg-white rounded-lg box-shadow">
                  <Disclosure defaultOpen>
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="disclosureBtn ml-2 flex justify-between">
                          <div className="disclosureLabel sm:text-sm xl:text-base gap-2 items-center mx-2 text-left flex">
                            {" "}
                            <ChevronRightIcon
                              className={`${
                                open ? "transform rotate-90" : ""
                              } w-5 h-5 text-gray-500`}
                            />{" "}
                            {"Business License & Permits"}
                          </div>
                          {userPermissions.includes(addDisabledBLP) && (
                            <Menu as="div" className="items-center flex">
                              <div>
                                <Menu.Button className="">
                                  <Bars2Icon
                                    className="-ml-10 mt-1 h-5 w-5 text-gray-900 "
                                    aria-hidden="true"
                                  />
                                </Menu.Button>
                              </div>
                              <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                              >
                                <Menu.Items className="absolute right-0 origin-top-right transform opacity-100 scale-100">
                                  <div className="mr-5 pt-8">
                                    <Menu.Item>
                                      {({ active }) => (
                                        <button
                                          onClick={() => onClickBLPAddButton()}
                                          className={`${
                                            active
                                              ? "bg-gray-100 text-black"
                                              : "text-gray-900"
                                          } cursor-pointer text-secondary py-1 px-2 flex items-center place-content-center
                                                              hover:scale-90 transition duration-500 shadow-md text-secondary group relative
                                                              px-2 underline text-xs font-medium hover:text-hsecondary focus:outline-none focus:ring-2
                                                              focus:ring-offset-2 focus:ring-secondary focus:ring-opacity-7`}
                                        >
                                          <PlusCircleIcon className="h-4 w-4 mr-2" />
                                          Add
                                        </button>
                                      )}
                                    </Menu.Item>
                                  </div>
                                </Menu.Items>
                              </Transition>
                            </Menu>
                          )}
                        </Disclosure.Button>

                        <Disclosure.Panel className="text-xs bg-white">
                          {
                            <OfficeBusinessLicensesandPermits
                              data={officeData}
                            />
                          }
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                </div>
              )}
              {userPermissions.includes(viewLeasedEquipment) && (
                <div className="mt-2 mb-2 mr-4 bg-white rounded-lg box-shadow">
                  <Disclosure defaultOpen>
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="disclosureBtn ml-2 flex text-base justify-between">
                          <div className="disclosureLabel sm:text-sm xl:text-base items-center flex gap-2 mx-2  ">
                            {" "}
                            <ChevronRightIcon
                              className={`${
                                open ? "transform rotate-90" : ""
                              } w-5 h-5 text-gray-500`}
                            />
                            <div className="sm:grid xl:flex xl:gap-1">
                              <p className="sm:w-24 lg:w-full xl:text-center sm:text-left">
                                {"Leased Equipment"}
                              </p>
                            </div>
                          </div>
                          {userPermissions.includes(addDisabledLE) && (
                            <Menu as="div" className="">
                              <div>
                                <Menu.Button className="">
                                  <Bars2Icon
                                    className="-ml-10 h-5 w-5 text-gray-900"
                                    aria-hidden="true"
                                  />
                                </Menu.Button>
                              </div>
                              <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                              >
                                <Menu.Items className="absolute right-0 origin-top-right transform opacity-100 scale-100">
                                  <div className="mr-5">
                                    <Menu.Item>
                                      {({ active }) => (
                                        <button
                                          onClick={() => onClickLEAddButton()}
                                          className={`${
                                            active
                                              ? "bg-gray-100 text-black"
                                              : "text-gray-900"
                                          } cursor-pointer text-secondary py-1 px-2 flex items-center place-content-center
                                                              hover:scale-90 transition duration-500 shadow-md text-secondary group relative
                                                              px-2 underline text-xs font-medium hover:text-hsecondary focus:outline-none focus:ring-2
                                                              focus:ring-offset-2 focus:ring-secondary focus:ring-opacity-7`}
                                        >
                                          <PlusCircleIcon className="h-4 w-4 mr-2" />
                                          Add
                                        </button>
                                      )}
                                    </Menu.Item>
                                  </div>
                                </Menu.Items>
                              </Transition>
                            </Menu>
                          )}
                        </Disclosure.Button>

                        <Disclosure.Panel className="text-xs bg-white">
                          {<OfficeLeasedEquipments data={officeData} />}
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                </div>
              )}
              {userPermissions.includes(viewSecurity) && (
                <div className="mt-2 mb-2 mr-4 bg-white rounded-lg box-shadow">
                  <Disclosure defaultOpen>
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="disclosureBtn ml-2 flex justify-between">
                          <div className="disclosureLabel sm:text-sm xl:text-base items-center flex gap-2 mx-2">
                            {" "}
                            <ChevronRightIcon
                              className={`${
                                open ? "transform rotate-90" : ""
                              } w-5 h-5 text-gray-500`}
                            />
                            {"Security"}
                          </div>
                          {userPermissions.includes(addDisabledSecurity) &&
                            disableSecurityAddButton === false && (
                              <Menu as="div" className="">
                                <div>
                                  <Menu.Button className="">
                                    <Bars2Icon
                                      className="-ml-10  h-5 w-5 text-gray-900 "
                                      aria-hidden="true"
                                    />
                                  </Menu.Button>
                                </div>
                                <Transition
                                  as={Fragment}
                                  enter="transition ease-out duration-100"
                                  enterFrom="transform opacity-0 scale-95"
                                  enterTo="transform opacity-100 scale-100"
                                  leave="transition ease-in duration-75"
                                  leaveFrom="transform opacity-100 scale-100"
                                  leaveTo="transform opacity-0 scale-95"
                                >
                                  <Menu.Items className=" absolute right-0 origin-top-right transform opacity-100 scale-100">
                                    <div className="mr-5">
                                      <Menu.Item>
                                        {({ active }) => (
                                          <button
                                            onClick={() =>
                                              onClickSecurityAddButton()
                                            }
                                            className={`${
                                              active
                                                ? "bg-gray-100 text-black"
                                                : "text-gray-900"
                                            }cursor-pointer text-secondary py-1 px-2 flex items-center place-content-center hover:scale-90
                                                                  transition duration-500 shadow-md text-secondary group relative px-2 underline text-xs font-medium
                                                                  hover:text-hsecondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary focus:ring-opacity-7`}
                                          >
                                            <PlusCircleIcon className="h-4 w-4 mr-2" />
                                            Add
                                          </button>
                                        )}
                                      </Menu.Item>
                                    </div>
                                  </Menu.Items>
                                </Transition>
                              </Menu>
                            )}
                        </Disclosure.Button>

                        <Disclosure.Panel className="text-xs bg-white">
                          {<OfficeSecurity data={officeData} />}
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                </div>
              )}
              {userPermissions.includes(viewOfficeVendor) && (
                <div className="mt-4 mb-2 mr-4 bg-white rounded-lg box-shadow">
                  <Disclosure defaultOpen>
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="disclosureBtn ml-2 flex justify-between">
                          <div className="disclosureLabel sm:text-sm xl:text-base items-center text-left flex gap-2 mx-2  ">
                            {" "}
                            <ChevronRightIcon
                              className={`${
                                open ? "transform rotate-90" : ""
                              } w-5 h-5 text-gray-500`}
                            />
                            {"Office Vendor Service"}
                          </div>
                          {userPermissions.includes(addOfficeVendor) && (
                            <Menu as="div" className="">
                              <div>
                                <Menu.Button className="">
                                  <Bars2Icon
                                    className="-ml-10  h-5 w-5 text-gray-900 "
                                    aria-hidden="true"
                                  />
                                </Menu.Button>
                              </div>
                              <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                              >
                                <Menu.Items className=" absolute right-0 origin-top-right rounded-full transform opacity-100 scale-100">
                                  <div className="mr-5">
                                    <Menu.Item>
                                      {({ active }) => (
                                        <button
                                          onClick={() =>
                                            onClickOfficeVendorModal()
                                          }
                                          className={`${
                                            active
                                              ? "bg-gray-100 text-black"
                                              : "text-gray-900"
                                          } cursor-pointer text-secondary  py-1 px-2 flex items-center place-content-center hover:scale-90
                                                              transition duration-500 shadow-md text-secondary group relative px-2 underline text-xs font-medium
                                                              hover:text-hsecondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary focus:ring-opacity-7`}
                                        >
                                          <PlusCircleIcon className="h-4 w-4 mr-2" />
                                          Add
                                        </button>
                                      )}
                                    </Menu.Item>
                                  </div>
                                </Menu.Items>
                              </Transition>
                            </Menu>
                          )}
                        </Disclosure.Button>
                        <Disclosure.Panel className="text-xs bg-white">
                          {<OfficeVendorSubModule data={officeData} />}
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                </div>
              )}
              {userPermissions.includes(viewAppliancePermission) && (
                <div className="mt-2 mb-2 mr-4 bg-white rounded-lg box-shadow">
                  <Disclosure defaultOpen>
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="disclosureBtn ml-2 flex text-base justify-between">
                          <div className="disclosureLabel sm:text-sm xl:text-base items-center flex gap-2 mx-2  ">
                            {" "}
                            <ChevronRightIcon
                              className={`${
                                open ? "transform rotate-90" : ""
                              } w-5 h-5 text-gray-500`}
                            />
                            <div className="sm:grid xl:flex xl:gap-1">
                              <p className="sm:w-24 lg:w-full xl:text-center sm:text-left">
                                {"Office Appliances"}
                              </p>
                            </div>
                          </div>
                          {userPermissions.includes(addAppliancePermission) && (
                            <Menu as="div" className="">
                              <div>
                                <Menu.Button className="">
                                  <Bars2Icon
                                    className="-ml-10 h-5 w-5 text-gray-900"
                                    aria-hidden="true"
                                  />
                                </Menu.Button>
                              </div>
                              <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                              >
                                <Menu.Items className="absolute right-0 origin-top-right transform opacity-100 scale-100">
                                  <div className="mr-5">
                                    <Menu.Item>
                                      {({ active }) => (
                                        <button
                                          onClick={() =>
                                            onClickAddOfficeAppliance()
                                          }
                                          className={`${
                                            active
                                              ? "bg-gray-100 text-black"
                                              : "text-gray-900"
                                          } cursor-pointer text-secondary py-1 px-2 flex items-center place-content-center
                                                              hover:scale-90 transition duration-500 shadow-md text-secondary group relative
                                                              px-2 underline text-xs font-medium hover:text-hsecondary focus:outline-none focus:ring-2
                                                              focus:ring-offset-2 focus:ring-secondary focus:ring-opacity-7`}
                                        >
                                          <PlusCircleIcon className="h-4 w-4 mr-2" />
                                          Add
                                        </button>
                                      )}
                                    </Menu.Item>
                                  </div>
                                </Menu.Items>
                              </Transition>
                            </Menu>
                          )}
                        </Disclosure.Button>

                        <Disclosure.Panel className="text-xs bg-white">
                          {<OfficeApplianceSideSection data={officeData} />}
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                </div>
              )}
            </div>
          </div>
          <OfficeVendorServiceModal
            showFullView={
              isOfficeVendorServiceAddState || isOfficeVendorServiceEditState
            }
            closeFullView={closeOfficeVendorModal}
            producerCodeId={0}
          >
            <OfficeVendorServiceForm data={officeData} />
          </OfficeVendorServiceModal>
        </>
      ) : (
        <div className="static">
          <CustomLoader />
        </div>
      )}
    </div>
  );
};

export default OfficeViews;
